import React, { useMemo, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  Search,
  Filter,
  Copy,
  ExternalLink,
  MessageCircle,
  ReceiptText,
  MapPin,
  X,
  Clock,
  ShieldCheck,
  AlertTriangle,
  Eye,
  Truck,
  Check,
  ChevronDown,
} from "lucide-react";
import { toast } from "react-toastify";
import { Ring } from "@uiball/loaders";
import Pagination from "../../../shared/components/Pagination";
import {
  getMyOrders,
  getMyOrdersAsBuyer,
  getMyApproval,
  getCustomOffers,
  acceptCustomOffer,
  rejectCustomOffer,
  updateOrderStatusSeller,
  uploadInvoice,
  approveOrder,
  rejectOrder,
  buyerApproveOrder,
  buyerRejectOrder,
  approveOrderBank,
  rejectOrderBank,
  buyerApproveOrderBank,
  buyeRejectOrderBank,
} from "../../../utils/services/user.service";
import { getBSCTxUrl } from "../../../utils/blockchainExplorer";
import SignaturePad from "./SignaturePad";
import PdfSignature from "./PdfSignature";
import WebCam from "../../../shared/components/WebCam/WebCam";
import { X as XIcon } from "lucide-react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import OfferModal from "../custom-offer/OfferModal";

const PageContainer = styled.div`
  min-height: 100vh;
  background: #0b1122;
  color: white;
  padding: 2.5rem;
`;

const Card = styled.div`
  padding: 18px;
  border-radius: 16px;
  background: #0d1220;
  border: 1px solid #1f2937;
`;

const OrderCard = styled.div`
  padding: ${(props) => (props.compact ? "12px" : "16px")};
  border-radius: 16px;
  background: #0d1220;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s ease-out;
  margin-bottom: ${(props) => (props.compact ? "8px" : "12px")};

  &:hover {
    border-color: rgba(0, 247, 210, 0.25);
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-1px);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.35);

    .action-buttons button {
      opacity: 1 !important;
      pointer-events: auto !important;
      transform: translateY(0) !important;
    }
  }

  ${(props) =>
    props.isActive &&
    `
    border-color: rgba(0, 247, 210, 0.25);
    background: rgba(0, 247, 210, 0.05);
    box-shadow: 0 0 0 1px rgba(0, 247, 210, 0.22), 0 0 36px rgba(0, 247, 210, 0.18);
    
    .action-buttons button {
      opacity: 1 !important;
      pointer-events: auto !important;
      transform: translateY(0) !important;
    }
  `}
`;

const OrderRowContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1.3fr 1.3fr 2fr 1.8fr 2fr 1fr;
  gap: 12px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const Chip = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #e5e7eb;
  display: inline-block;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid
    ${(props) => {
      const status = (props.status || "").toLowerCase();
      if (status === "pending" || status === "waiting")
        return "rgba(255, 255, 255, 0.1)";
      if (status === "paid" || status === "completed")
        return "rgba(34, 197, 94, 0.2)";
      if (status === "in escrow") return "rgba(139, 92, 246, 0.2)";
      if (status === "shipped") return "rgba(56, 189, 248, 0.2)";
      if (
        status === "disputed" ||
        status === "rejected" ||
        status === "declined" ||
        status === "failed"
      )
        return "rgba(251, 191, 36, 0.2)";
      return "rgba(255, 255, 255, 0.1)";
    }};
  background: ${(props) => {
    const status = (props.status || "").toLowerCase();
    if (status === "pending" || status === "waiting")
      return "rgba(255, 255, 255, 0.05)";
    if (status === "paid" || status === "completed")
      return "rgba(34, 197, 94, 0.1)";
    if (status === "in escrow") return "rgba(139, 92, 246, 0.1)";
    if (status === "shipped") return "rgba(56, 189, 248, 0.1)";
    if (
      status === "disputed" ||
      status === "rejected" ||
      status === "declined" ||
      status === "failed"
    )
      return "rgba(251, 191, 36, 0.1)";
    return "rgba(255, 255, 255, 0.05)";
  }};
  color: #e5e7eb;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => {
    const status = (props.status || "").toLowerCase();
    if (status === "pending" || status === "waiting")
      return "rgba(255, 255, 255, 0.4)";
    if (status === "paid" || status === "completed") return "#22c55e";
    if (status === "in escrow") return "#a78bfa";
    if (status === "shipped") return "#38bdf8";
    if (
      status === "disputed" ||
      status === "rejected" ||
      status === "declined" ||
      status === "failed"
    )
      return "#fbbf24";
    return "rgba(255, 255, 255, 0.4)";
  }};
  box-shadow: ${(props) => {
    const status = (props.status || "").toLowerCase();
    if (status === "paid" || status === "completed")
      return "0 0 12px rgba(34, 197, 94, 0.25)";
    if (status === "in escrow") return "0 0 12px rgba(139, 92, 246, 0.25)";
    if (status === "shipped") return "0 0 12px rgba(56, 189, 248, 0.25)";
    return "0 0 12px rgba(255, 255, 255, 0.25)";
  }};
`;

const Button = styled.button`
  padding: 8px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #e5e7eb;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  min-width: 32px;
  height: 32px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  ${(props) =>
    props.primary &&
    `
    background: rgba(0, 247, 210, 0.15);
    border-color: rgba(0, 247, 210, 0.2);
    box-shadow: 0 0 24px rgba(0, 247, 210, 0.18);
    
    &:hover {
      background: rgba(0, 247, 210, 0.2);
    }
  `}

  ${(props) =>
    props.active &&
    `
    background: rgba(255, 255, 255, 0.1);
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleButton = styled.button`
  padding: 8px 16px;
  border-radius: 999px;
  background: ${(props) =>
    props.active ? "rgba(0, 247, 210, 0.15)" : "rgba(255, 255, 255, 0.05)"};
  border: 1px solid
    ${(props) =>
      props.active ? "rgba(0, 247, 210, 0.25)" : "rgba(255, 255, 255, 0.1)"};
  color: #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "400")};

  &:hover {
    background: ${(props) =>
      props.active ? "rgba(0, 247, 210, 0.22)" : "rgba(255, 255, 255, 0.1)"};
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  padding: 8px 16px;
  border-radius: 999px;
  background: ${(props) =>
    props.active ? "rgba(0, 247, 210, 0.15)" : "rgba(255, 255, 255, 0.05)"};
  border: 1px solid
    ${(props) =>
      props.active ? "rgba(0, 247, 210, 0.25)" : "rgba(255, 255, 255, 0.1)"};
  color: #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  min-width: 160px;

  &:hover {
    background: ${(props) =>
      props.active ? "rgba(0, 247, 210, 0.22)" : "rgba(255, 255, 255, 0.1)"};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: #0d1220;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 8px;
  min-width: 180px;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  display: ${(props) => (props.open ? "block" : "none")};
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${(props) =>
    props.active ? "rgba(0, 247, 210, 0.15)" : "transparent"};
  border: none;
  color: ${(props) => (props.active ? "#00f7d2" : "#e5e7eb")};
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  transition: all 0.2s ease;
  margin-bottom: 4px;

  &:hover {
    background: ${(props) =>
      props.active ? "rgba(0, 247, 210, 0.22)" : "rgba(255, 255, 255, 0.05)"};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #e5e7eb;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: rgba(0, 247, 210, 0.3);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Select = styled.select`
  padding: 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #e5e7eb;
  font-size: 14px;
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: rgba(0, 247, 210, 0.3);
  }
`;

const Panel = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
`;

const PanelOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
`;

const PanelContent = styled.div`
  position: absolute;
  right: 12px;
  left: 12px;
  top: 12px;
  bottom: 12px;
  background: #0d1220;
  border: 1px solid #1f2937;
  border-radius: 18px;
  box-shadow:
    0 0 0 1px rgba(0, 247, 210, 0.25),
    0 0 32px rgba(0, 247, 210, 0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    left: auto;
    width: 560px;
  }
`;

const ApproveSidebar = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  height: 100vh;
  background: #0d1220;
  border-left: 1px solid #1f2937;
  transform: ${({ open }) => (open ? "translateX(0)" : "translateX(110%)")};
  transition: 0.35s ease;
  padding: 24px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const RejectSidebar = styled(ApproveSidebar)``;

const OrderDetailSidebar = styled(ApproveSidebar)`
  width: 600px;
  overflow: hidden;
  overflow-y: hidden;
  z-index: 100051;
`;

const SidebarOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 100050;
  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? "visible" : "hidden")};
  transition:
    opacity 0.35s ease,
    visibility 0.35s ease;
`;

const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-shrink: 0;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  color: #e5e7eb;
  font-size: 20px;
`;

const ApproverTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #0d1220;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #1f2937;
`;

const ApproverTableHeader = styled.thead`
  background: rgba(255, 255, 255, 0.05);
`;

const ApproverTableHeaderRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ApproverTableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ApproverTableBody = styled.tbody``;

const ApproverTableRowStyled = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ApproverTableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #e5e7eb;
  vertical-align: middle;
`;

const StatusChipDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => {
    const s = (props.status || "").toLowerCase();
    if (s === "waiting" || s === "pending") return "#4b5563"; // Dark gray for waiting/pending
    if (s === "paid" || s === "completed" || s === "approved") return "#22c55e";
    if (s === "rejected" || s === "declined" || s === "failed")
      return "#ef4444";
    return "#4b5563"; // Default dark gray
  }};
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: ${(props) => {
    const s = (props.status || "").toLowerCase();
    if (s === "waiting" || s === "pending") return "rgba(255, 255, 255, 0.05)";
    if (s === "paid" || s === "completed" || s === "approved")
      return "rgba(34, 197, 94, 0.15)";
    if (s === "rejected" || s === "declined" || s === "failed")
      return "rgba(239, 68, 68, 0.15)";
    return "rgba(255, 255, 255, 0.05)";
  }};
  border: 1px solid
    ${(props) => {
      const s = (props.status || "").toLowerCase();
      if (s === "waiting" || s === "pending") return "rgba(255, 255, 255, 0.1)";
      if (s === "paid" || s === "completed" || s === "approved")
        return "rgba(34, 197, 94, 0.3)";
      if (s === "rejected" || s === "declined" || s === "failed")
        return "rgba(239, 68, 68, 0.3)";
      return "rgba(255, 255, 255, 0.1)";
    }};
  color: #e5e7eb;
`;

const ActionButtonGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  color: #e5e7eb;

  ${(props) =>
    props.active &&
    `
    background: rgba(0, 247, 210, 0.15);
    border: 1px solid rgba(0, 247, 210, 0.3);
    color: #00f7d2;
    
    svg {
      color: #00f7d2;
    }
  `}

  &:hover {
    background: ${(props) =>
      props.active ? "rgba(0, 247, 210, 0.2)" : "rgba(255, 255, 255, 0.08)"};
    border-color: ${(props) =>
      props.active ? "rgba(0, 247, 210, 0.4)" : "rgba(255, 255, 255, 0.15)"};
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${(props) => (props.active ? "#00f7d2" : "#e5e7eb")};
  }
`;

const SidebarCloseButton = styled.button`
  background: transparent;
  border: none;
  color: #9ca3af;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #e5e7eb;
  }
`;

const SidebarFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const SidebarLabel = styled.label`
  font-size: 13px;
  color: #9ca3af;
  opacity: 0.7;
`;

const SidebarTextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  background: #0d1220;
  border: 1px solid #1f2937;
  color: #e5e7eb;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
`;

const SidebarFileInput = styled.input`
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  background: #0d1220;
  border: 1px solid #1f2937;
  color: #e5e7eb;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
`;

const SidebarSubmitButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  background: ${(props) =>
    props.disabled ? "#1f2937" : "linear-gradient(90deg, #00f7d2, #5ce9ff)"};
  color: ${(props) => (props.disabled ? "#777" : "#000")};
  font-weight: 700;
  font-size: 14px;
  border: none;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const CollapsibleCell = styled.div`
  overflow: hidden;
  transition: all 0.2s ease-out;
  ${(props) =>
    props.show
      ? `
    max-height: ${props.maxH || "96px"};
    opacity: 1;
  `
      : `
    max-height: 0;
    opacity: 0;
    pointer-events: none;
  `}
`;

const shortHash = (h) => {
  if (!h) return "—";
  return h.length <= 12 ? h : `${h.slice(0, 6)}…${h.slice(-4)}`;
};

const bscscan = (tx) => {
  if (!tx) return "#";
  return getBSCTxUrl(tx);
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

function StatusBadgeComponent({ status }) {
  const getIcon = () => {
    const s = (status || "").toLowerCase();
    if (s === "pending" || s === "waiting" || s === "active")
      return <Clock size={14} style={{ opacity: 0.8 }} />;
    if (s === "paid" || s === "completed" || s === "accepted")
      return <ShieldCheck size={14} style={{ opacity: 0.8 }} />;
    if (s === "in escrow" || s === "shipped")
      return <MapPin size={14} style={{ opacity: 0.8 }} />;
    if (
      s === "disputed" ||
      s === "rejected" ||
      s === "declined" ||
      s === "failed" ||
      s === "reject"
    )
      return <AlertTriangle size={14} style={{ opacity: 0.8 }} />;
    return <Clock size={14} style={{ opacity: 0.8 }} />;
  };

  return (
    <StatusBadge status={status}>
      <StatusDot status={status} />
      {getIcon()}
      <span
        style={{
          letterSpacing: "0.5px",
          fontWeight: 500,
          textTransform: "capitalize",
        }}
      >
        {status || "Unknown"}
      </span>
    </StatusBadge>
  );
}

function CollapsibleCellComponent({
  show,
  delayOnOpen = true,
  maxH = "96px",
  children,
}) {
  const delay = show && delayOnOpen ? "75ms" : "0ms";
  return (
    <CollapsibleCell show={show} maxH={maxH} style={{ transitionDelay: delay }}>
      <div style={{ paddingTop: "2px" }}>{children}</div>
    </CollapsibleCell>
  );
}

function GhostChip({ order, activeMode }) {
  const label = activeMode ? activeMode.toUpperCase() : "OPEN";
  return (
    <Chip
      style={{
        borderColor: "rgba(0, 247, 210, 0.2)",
        background: "rgba(0, 247, 210, 0.1)",
      }}
    >
      <StatusDot status="paid" />
      <span style={{ fontSize: "11px", letterSpacing: "0.5px" }}>{label}</span>
      <span style={{ fontSize: "11px" }}>
        {order.order_status || "Unknown"}
      </span>
      <span style={{ fontSize: "11px", opacity: 0.8 }}>•</span>
      <span style={{ fontSize: "11px", fontFamily: "monospace" }}>
        {order.txHash ? shortHash(order.txHash) : shortHash(order._id)}
      </span>
    </Chip>
  );
}

// Reusable Detail Field Component
function DetailField({ label, value, icon: Icon, align = "center" }) {
  return (
    <div style={{ textAlign: align }}>
      <div
        style={{
          fontSize: "12px",
          color: "rgba(255, 255, 255, 0.7)",
          marginBottom: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: align === "center" ? "center" : "flex-start",
          gap: "4px",
        }}
      >
        {label} {Icon && <Icon size={12} />}
      </div>
      <div
        style={{ fontSize: "14px", color: "#e5e7eb", wordBreak: "break-word" }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

// Reusable Detail Section Component
function DetailSection({ title, fields, showCondition = true }) {
  if (!showCondition) return null;

  return (
    <SidebarFormGroup>
      <SidebarLabel
        style={{
          fontSize: "16px",
          fontWeight: 600,
          marginBottom: "12px",
          color: "#e5e7eb",
        }}
      >
        {title}
      </SidebarLabel>
      <Card
        style={{
          padding: "16px",
          border: "1px solid #1f2937",
          borderRadius: "8px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "16px",
          }}
        >
          {fields.map((field, idx) => (
            <DetailField
              key={idx}
              label={field.label}
              value={field.value}
              icon={field.icon}
              align={field.align || "center"}
            />
          ))}
        </div>
      </Card>
    </SidebarFormGroup>
  );
}

// Invoice Table Component
function InvoiceTable({ products, symbol, network }) {
  return (
    <SidebarFormGroup>
      <SidebarLabel
        style={{
          fontSize: "16px",
          fontWeight: 600,
          marginBottom: "12px",
          color: "#e5e7eb",
        }}
      >
        Invoice
      </SidebarLabel>
      <Card
        style={{
          padding: 0,
          border: "1px solid #1f2937",
          borderRadius: "8px",
          overflow: "hidden",
          background: "#0d1220",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#374151", color: "#e5e7eb" }}>
              <th
                style={{
                  padding: "12px",
                  textAlign: "left",
                  fontSize: "12px",
                  fontWeight: 600,
                  borderBottom: "1px solid #1f2937",
                }}
              >
                Product
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontSize: "12px",
                  fontWeight: 600,
                  borderBottom: "1px solid #1f2937",
                }}
              >
                Qty
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontSize: "12px",
                  fontWeight: 600,
                  borderBottom: "1px solid #1f2937",
                }}
              >
                Color
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "center",
                  fontSize: "12px",
                  fontWeight: 600,
                  borderBottom: "1px solid #1f2937",
                }}
              >
                Discount
              </th>
              <th
                style={{
                  padding: "12px",
                  textAlign: "right",
                  fontSize: "12px",
                  fontWeight: 600,
                  borderBottom: "1px solid #1f2937",
                }}
              >
                Price
              </th>
            </tr>
          </thead>
          <tbody>
            {products && products.length > 0 ? (
              products.map((product, idx) => {
                const productName =
                  product.title || product.product_name || product.name || "—";
                const quantity =
                  product.quantity != null ? product.quantity : "—";
                const color = product.color || "—";
                const discount =
                  product.discount != null ? `${product.discount}%` : "—";
                const price =
                  product.price != null
                    ? `${product.total_amount} ${symbol || "USDT"} (${network || "BEP20"})`
                    : "—";

                return (
                  <tr
                    key={product._id || idx}
                    style={{
                      background: "#0d1220",
                      borderBottom:
                        idx < products.length - 1
                          ? "1px solid #1f2937"
                          : "none",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px",
                        fontSize: "14px",
                        color: "#e5e7eb",
                        textTransform: "capitalize",
                      }}
                    >
                      {productName}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontSize: "14px",
                        color: "#e5e7eb",
                        textAlign: "center",
                      }}
                    >
                      {quantity}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontSize: "14px",
                        color: "#e5e7eb",
                        textAlign: "center",
                        textTransform: "capitalize",
                      }}
                    >
                      {color}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontSize: "14px",
                        color: "#e5e7eb",
                        textAlign: "center",
                      }}
                    >
                      {discount}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        fontSize: "14px",
                        color: "#e5e7eb",
                        textAlign: "right",
                      }}
                    >
                      {price}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr style={{ background: "#0d1220" }}>
                <td
                  style={{
                    padding: "12px",
                    fontSize: "14px",
                    color: "#e5e7eb",
                    textAlign: "left",
                  }}
                >
                  —
                </td>
                <td
                  style={{
                    padding: "12px",
                    fontSize: "14px",
                    color: "#e5e7eb",
                    textAlign: "center",
                  }}
                >
                  —
                </td>
                <td
                  style={{
                    padding: "12px",
                    fontSize: "14px",
                    color: "#e5e7eb",
                    textAlign: "center",
                  }}
                >
                  —
                </td>
                <td
                  style={{
                    padding: "12px",
                    fontSize: "14px",
                    color: "#e5e7eb",
                    textAlign: "center",
                  }}
                >
                  —
                </td>
                <td
                  style={{
                    padding: "12px",
                    fontSize: "14px",
                    color: "#e5e7eb",
                    textAlign: "right",
                  }}
                >
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </SidebarFormGroup>
  );
}

function ApproverTableRow({
  order,
  onOpen,
  onApprovePayment,
  onRejectPayment,
  onApproveOrder,
  onRejectOrder,
  onViewOrder,
}) {
  const paymentStatus =
    order.Payment_status || order.payment_status || "Waiting";
  const approvalStatus =
    order.is_approved_status || order.approval_status || "Pending";
  const buyerApprovalStatus = order.buyer_is_approved_status || "-";
  const orderStatus = order.order_status || "Waiting";
  const quantity =
    order.product_details?.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    ) ||
    order.quantity ||
    1;
  const total = order.total_amount
    ? `${parseFloat(order.total_amount).toFixed(4)} ${order.symbol || "USDT"} (${order.network || "BEP20"})`
    : "—";

  // Check if approval is pending
  const isApprovalPending = order.is_approved_status === "pending";
  const isBuyerApprovalPending = order.buyer_is_approved_status === "pending";

  const approveTooltip = (props) => (
    <Tooltip id="approve-tooltip" {...props}>
      Approve
    </Tooltip>
  );

  const rejectTooltip = (props) => (
    <Tooltip id="reject-tooltip" {...props}>
      Reject
    </Tooltip>
  );

  const buyerApproveTooltip = (props) => (
    <Tooltip id="buyer-approve-tooltip" {...props}>
      Buyer Approve
    </Tooltip>
  );

  const buyerRejectTooltip = (props) => (
    <Tooltip id="buyer-reject-tooltip" {...props}>
      Buyer Reject
    </Tooltip>
  );

  const viewTooltip = (props) => (
    <Tooltip id="view-tooltip" {...props}>
      View
    </Tooltip>
  );

  const trackTooltip = (props) => (
    <Tooltip id="track-tooltip" {...props}>
      Track Order
    </Tooltip>
  );

  return (
    <ApproverTableRowStyled>
      <ApproverTableCell>{order._id || order.order_no}</ApproverTableCell>
      <ApproverTableCell>{quantity}</ApproverTableCell>
      <ApproverTableCell>{total}</ApproverTableCell>
      <ApproverTableCell>
        <StatusChip status={paymentStatus}>
          <StatusChipDot status={paymentStatus} />
          {paymentStatus}
        </StatusChip>
      </ApproverTableCell>
      <ApproverTableCell>
        <StatusChip status={approvalStatus}>
          <StatusChipDot status={approvalStatus} />
          {approvalStatus}
        </StatusChip>
      </ApproverTableCell>
      <ApproverTableCell>
        {buyerApprovalStatus === "-" ? (
          "-"
        ) : (
          <StatusChip status={buyerApprovalStatus}>
            <StatusChipDot status={buyerApprovalStatus} />
            {buyerApprovalStatus}
          </StatusChip>
        )}
      </ApproverTableCell>
      <ApproverTableCell>
        <StatusChip status={orderStatus}>
          <StatusChipDot status={orderStatus} />
          {orderStatus}
        </StatusChip>
      </ApproverTableCell>
      <ApproverTableCell>
        <ActionButtonGroup>
          {isApprovalPending && (
            <>
              <OverlayTrigger placement="top" overlay={approveTooltip}>
                <ActionButton onClick={() => onApproveOrder(order)}>
                  <Check size={18} />
                </ActionButton>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={rejectTooltip}>
                <ActionButton onClick={() => onRejectOrder(order)}>
                  <XIcon size={18} />
                </ActionButton>
              </OverlayTrigger>
            </>
          )}
          {isBuyerApprovalPending && (
            <>
              <OverlayTrigger placement="top" overlay={buyerApproveTooltip}>
                <ActionButton onClick={() => onApproveOrder(order)}>
                  <Check size={18} />
                </ActionButton>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={buyerRejectTooltip}>
                <ActionButton onClick={() => onRejectOrder(order)}>
                  <XIcon size={18} />
                </ActionButton>
              </OverlayTrigger>
            </>
          )}
          <OverlayTrigger placement="top" overlay={viewTooltip}>
            <ActionButton onClick={() => onViewOrder(order)}>
              <Eye size={18} />
            </ActionButton>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={trackTooltip}>
            <ActionButton onClick={() => onOpen("track", order)}>
              <MapPin size={18} />
            </ActionButton>
          </OverlayTrigger>
        </ActionButtonGroup>
      </ApproverTableCell>
    </ApproverTableRowStyled>
  );
}

function CustomOfferCard({ offer, onAccept, onReject, onView, loggedInUser }) {

  const isSeller = offer?.seller_user_id === loggedInUser?.user_id;
  const isActive = offer?.offer_status === "active";
  const canAcceptReject = isSeller && isActive;

  const copy = async () => {
    const hash = offer._id;
    try {
      await navigator.clipboard.writeText(hash);
      toast.success("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const buyerName =  loggedInUser?.name || "Unknown";
  const sellerName =
    offer?.seller_wallet?.[0]?.name ||
    offer?.seller_wallet?.name ||
    offer?.seller_info?.name ||
    offer?.seller_name ||
    offer?.seller_detail?.[0]?.name ||
    offer?.store_detail?.[0]?.storeName ||
    "Unknown";
  const displayName = isSeller ? buyerName : sellerName;
  const role = isSeller ? "Buyer" : "Seller";
  const symbol = offer?.symbol || "USDT";
  const network = offer?.network_name || "BEP20";

  return (
    <OrderCard compact>
      <OrderRowContainer
        style={{ gap: "12px", gridTemplateColumns: "2fr 2fr 2fr 2fr 2fr 1fr" }}
      >
        {/* Column 1: Offer ID & Date */}
        <div>
          <div style={{ fontSize: "14px", fontWeight: 600 }}>
            {offer._id || "N/A"}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              marginTop: "2px",
            }}
          >
            {formatDate(
              offer.createdAt || offer.created_at || offer.created_date
            )}
          </div>
        </div>

        {/* Column 2: Buyer/Seller Name & Role */}
        <div>
          <div style={{ fontSize: "14px" }}>{displayName}</div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              marginTop: "2px",
            }}
          >
            {role} • {symbol} ({network})
          </div>
        </div>

        {/* Column 3: Offer Amount */}
        <div>
          <div style={{ fontSize: "14px", fontWeight: 600 }}>
            {offer.custom_offer_amount
              ? `${parseFloat(offer.custom_offer_amount).toFixed(4)} ${symbol}`
              : "—"}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              marginTop: "2px",
            }}
          >
            ≈{" "}
            {offer.custom_offer_amount
              ? parseFloat(offer.custom_offer_amount).toLocaleString()
              : "0"}{" "}
            {offer.fiat_currency || "EUR"}
          </div>
        </div>

        {/* Column 4: Status & KYC/Escrow */}
        <div>
          <div style={{ marginBottom: "4px" }}>
            <StatusBadgeComponent status={offer.offer_status || "waiting"} />
          </div>
          <div style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.7)" }}>
            KYC: {offer.kyc_status || "N/A"} • Escrow:{" "}
            {offer.escrow_status || "N/A"}
          </div>
        </div>

        {/* Column 5: Transaction Hash/Offer ID */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                color: "#e5e7eb",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                maxWidth: "150px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {shortHash(offer._id)}
            </span>
            <a
              href={bscscan(
                offer.txHash || offer.transaction_hash || offer.order_transaction_hash
              )}
              target="_blank"
              rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center" }}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink
                size={14}
                style={{ opacity: 0.7, color: "#e5e7eb" }}
              />
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copy();
              }}
              style={{
                padding: "4px",
                minWidth: "auto",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
              }}
              title="Copy offer ID"
            >
              <Copy size={14} style={{ opacity: 0.7, color: "#e5e7eb" }} />
            </button>
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              marginTop: "2px",
            }}
          >
            Block: {offer.block_number || offer.blockNumber || "—"}
          </div>
        </div>

        {/* Column 6: Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <div
            className="action-buttons"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            {canAcceptReject && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAccept(offer);
                  }}
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    background: "rgba(34, 197, 94, 0.1)",
                    color: "#22c55e",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Accept Offer"
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReject(offer);
                  }}
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    background: "rgba(239, 68, 68, 0.1)",
                    color: "#ef4444",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Reject Offer"
                >
                  <X size={16} />
                </button>
              </>
            )}
            {onView && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView(offer);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  background: "rgba(255, 255, 255, 0.05)",
                  color: "#e5e7eb",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="View offer details"
              >
                <Eye size={16} />
              </button>
            )}
          </div>
        </div>
      </OrderRowContainer>
    </OrderCard>
  );
}

function OrderRow({
  order,
  onOpen,
  isActive,
  activeMode,
  viewMode,
  onApprove,
  onReject,
}) {
  const copy = async () => {
    const hash = order.txHash || order.transaction_hash || order._id;
    try {
      await navigator.clipboard.writeText(hash);
      toast.success("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // When viewing as buyer, counterparty is seller; when viewing as seller, counterparty is buyer
  const isBuyerView = viewMode === "buyer";
  const counterpartyName = isBuyerView
    ? (order.seller_wallet?.[0]?.name ||
       order.seller_wallet?.name ||
       order.seller_info?.name ||
       order.seller_name ||
       order.seller_detail?.[0]?.name ||
       order.store_detail?.[0]?.storeName ||
       "Unknown")
    : (order.buyer_info?.name ||
       order.buyer_name ||
       order.buyer_detail?.[0]?.name ||
       "Unknown");
  const counterpartyRole = isBuyerView ? "Seller" : "Buyer";
  const network = order.network || order.symbol || "BNB";
  const txHash =
    order.txHash ||
    order.transaction_hash ||
    order.payment_transaction_hash ||
    order.order_transaction_hash;
  const orderStatus = order.Payment_status || "Pending";
  const isPendingApproval =
    order.is_approved_status === "pending" ||
    order.buyer_is_approved_status === "pending";

  return (
    <OrderCard isActive={isActive} className="order-row-group">
      <OrderRowContainer>
        {/* Column 1: Order ID & Date */}
        <div>
          <div style={{ fontSize: "14px", fontWeight: 600 }}>
            {order._id || order.order_no}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              marginTop: "4px",
            }}
          >
            {formatDate(order.createdAt || order.created_at)}
          </div>
        </div>

        {/* Column 2: Counterparty */}
        <div>
          <div style={{ fontSize: "14px" }}>{counterpartyName}</div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              marginTop: "4px",
            }}
          >
            {counterpartyRole} • {network}
          </div>
        </div>

        {/* Column 3: Payment Method */}
        <div>
          <div
            style={{
              fontSize: "13px",
              color: "#67e8f9",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              background: "rgba(103, 232, 249, 0.1)",
              borderRadius: "8px",
              border: "1px solid rgba(103, 232, 249, 0.2)",
              textWrap:"nowrap"
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M2 10h20" />
            </svg>
            {order.wallet_type === "internal" ? "Virtual Wallet" : order.wallet_type === "onchain" ? "On-chain Wallet" : (order.wallet_type || "N/A")}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "rgba(255, 255, 255, 0.5)",
              marginTop: "4px",
            }}
          >
            Payment Method
          </div>
        </div>

        {/* Column 4: Order Type */}
        <div>
          <div
            style={{
              fontSize: "13px",
              color: order.order_type === "group" ? "#a78bfa" : "#34d399",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              background: order.order_type === "group" ? "rgba(167, 139, 250, 0.1)" : "rgba(52, 211, 153, 0.1)",
              borderRadius: "8px",
              border: order.order_type === "group" ? "1px solid rgba(167, 139, 250, 0.2)" : "1px solid rgba(52, 211, 153, 0.2)",
              textTransform: "capitalize",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {order.order_type === "group" ? (
                <>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </>
              ) : (
                <>
                  <circle cx="9" cy="7" r="4" />
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                </>
              )}
            </svg>
            {order.order_type || "N/A"}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "rgba(255, 255, 255, 0.5)",
              marginTop: "4px",
            }}
          >
            Order Type
          </div>
        </div>

        {/* Column 5: Amount */}
        <div>
          <div style={{ fontSize: "14px", fontWeight: 600 }}>
            {order.total_amount
              ? `${parseFloat(order.total_amount).toFixed(4)} ${order.symbol || "USDT"}`
              : "—"}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              marginTop: "4px",
            }}
          >
            ≈{" "}
            {order.total_amount
              ? parseFloat(order.total_amount).toLocaleString()
              : "—"}{" "}
            {order.fiat_currency || "EUR"}
          </div>
          {(() => {
            const totalAmount = parseFloat(order.total_amount || 0);
            const shipping = parseFloat(order.total_delivery_charges || 0);
            const vat = parseFloat(order.vat_tax || 0);
            const salesTax = parseFloat(order.sale_tax || 0);
            const totalPaid = totalAmount + shipping + vat + salesTax;
            if (totalPaid > totalAmount) {
              return (
                <div
                  style={{
                    fontSize: "12px",
                    color: "rgba(255, 255, 255, 0.9)",
                    marginTop: "6px",
                    fontWeight: 500,
                    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                    paddingTop: "6px",
                  }}
                >
                  Total Paid: {totalPaid.toFixed(4)} {order.symbol || "USDT"}
                </div>
              );
            }
            return null;
          })()}
          {isActive && (
            <div style={{ marginTop: "8px" }}>
              <GhostChip order={order} activeMode={activeMode} />
            </div>
          )}
        </div>

        {/* Column 6: Status */}
        <div style={{ display: isActive ? "none" : "block" }}>
          <div style={{ marginBottom: "4px" }}>
            <StatusBadgeComponent status={orderStatus} />
          </div>
          <div style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.7)" }}>
            KYC: {order.kyc_status || order.kycStatus || "N/A"} • Escrow:{" "}
            {order.escrow_status || order.escrowStatus || "N/A"}
          </div>
        </div>

        {/* Column 7: Transaction Hash */}
        <div style={{ display: isActive ? "none" : "block" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              overflow: "hidden",
            }}
          >
            <a
              href={bscscan(txHash)}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: "14px",
                textDecoration: "none",
                color: "#e5e7eb",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                maxWidth: "150px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {shortHash(txHash)}{" "}
              <ExternalLink size={14} style={{ opacity: 0.7 }} />
            </a>
            <Button
              onClick={copy}
              style={{ padding: "6px", minWidth: "auto" }}
              title="Copy hash"
            >
              <Copy size={14} />
            </Button>
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.7)",
              marginTop: "4px",
            }}
          >
            Block: {order.block_number || "—"}
          </div>
        </div>

        {/* Column 6: Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <div
            className="action-buttons"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            {viewMode === "approver" && isPendingApproval ? (
              <>
                <Button
                  title="Approve"
                  onClick={() => onApprove(order)}
                  style={{
                    padding: "8px 12px",
                    background: "rgba(34, 197, 94, 0.15)",
                    border: "1px solid rgba(34, 197, 94, 0.25)",
                    color: "#22c55e",
                  }}
                >
                  <Check size={16} />
                </Button>
                <Button
                  title="Reject"
                  onClick={() => onReject(order)}
                  style={{
                    padding: "8px 12px",
                    background: "rgba(239, 68, 68, 0.15)",
                    border: "1px solid rgba(239, 68, 68, 0.25)",
                    color: "#ef4444",
                  }}
                >
                  <XIcon size={16} />
                </Button>
                <Button
                  title="View"
                  onClick={() => onOpen("view", order)}
                  style={{
                    padding: "8px",
                  }}
                >
                  <Eye size={16} />
                </Button>
              </>
            ) : (
              <>
                <Button
                  title="Chat"
                  active={isActive && activeMode === "chat"}
                  onClick={() => onOpen("chat", order)}
                  style={{
                    opacity: 0,
                    pointerEvents: "none",
                    transform: "translateY(2px)",
                    transition: "all 0.2s ease-out",
                    padding: "8px",
                  }}
                >
                  <MessageCircle size={16} />
                  {order.unread_count > 0 && (
                    <span
                      style={{
                        marginLeft: "4px",
                        padding: "2px 6px",
                        fontSize: "10px",
                        borderRadius: "999px",
                        background: "rgba(0, 247, 210, 0.2)",
                        border: "1px solid rgba(0, 247, 210, 0.2)",
                      }}
                    >
                      {order.unread_count}
                    </span>
                  )}
                </Button>

                <Button
                  title="Invoice"
                  active={isActive && activeMode === "invoice"}
                  onClick={() => onOpen("invoice", order)}
                  style={{
                    opacity: 0,
                    pointerEvents: "none",
                    transform: "translateY(2px)",
                    transition: "all 0.2s ease-out",
                    transitionDelay: "100ms",
                    padding: "8px",
                  }}
                >
                  <ReceiptText size={16} />
                </Button>

                <Button
                  title="View"
                  active={isActive && activeMode === "view"}
                  onClick={() => onOpen("view", order)}
                  style={{
                    opacity: 0,
                    pointerEvents: "none",
                    transform: "translateY(2px)",
                    transition: "all 0.2s ease-out",
                    transitionDelay: "150ms",
                    padding: "8px",
                  }}
                >
                  <Eye size={16} />
                </Button>

                <Button
                  primary
                  title="Track"
                  active={isActive && activeMode === "track"}
                  onClick={() => onOpen("track", order)}
                  style={{
                    opacity: 0,
                    pointerEvents: "none",
                    transform: "translateY(2px)",
                    transition: "all 0.2s ease-out",
                    transitionDelay: "200ms",
                    padding: "8px",
                  }}
                >
                  <MapPin size={16} />
                </Button>
              </>
            )}
          </div>
        </div>
      </OrderRowContainer>
    </OrderCard>
  );
}

function ActionPanel({
  open,
  mode,
  order,
  onClose,
  onUpdate,
  viewMode,
  onRefetch,
  setIsLoading,
}) {
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (open && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [open, order?._id]);

  if (!open || !order) return null;

  return (
    <Panel>
      <PanelOverlay onClick={onClose} />
      <PanelContent onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            padding: "16px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ fontSize: "14px", fontWeight: 600 }}>
              {order._id || order.order_no} •{" "}
              {viewMode === "buyer" 
                ? (order.seller_wallet?.[0]?.name || order.seller_wallet?.name || order.seller_info?.name || order.seller_name || order.store_detail?.[0]?.storeName || "Unknown")
                : (order.buyer_info?.name || order.buyer_name || order.buyer_detail?.[0]?.name || "Unknown")}
            </div>
            <div
              style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.7)" }}
            >
              {formatDate(order.createdAt || order.created_at)} •{" "}
              {order.total_amount
                ? parseFloat(order.total_amount).toLocaleString()
                : "—"}{" "}
              {order.fiat_currency || "EUR"} •{" "}
              {order.network || order.symbol || "BNB"}
            </div>
          </div>
          <Button
            onClick={onClose}
            style={{ padding: "10px", minWidth: "auto" }}
          >
            <X size={16} />
          </Button>
        </div>

        <div 
          ref={scrollContainerRef}
          style={{ 
            padding: "16px", 
            overflowY: "auto", 
            overflowX: "hidden",
            flex: 1,
            minHeight: 0
          }}
        >
          {mode === "track" && <TrackView order={order} />}
          {mode === "invoice" && (
            <InvoiceView
              order={order}
              onUpdate={onUpdate}
              viewMode={viewMode}
              onClose={onClose}
              onRefetch={onRefetch}
              setIsLoading={setIsLoading}
            />
          )}
          {mode === "chat" && <ChatView order={order} />}
          {mode === "view" && (
            <OrderView
              order={order}
              onUpdate={onUpdate}
              setIsLoading={setIsLoading}
              viewMode={viewMode}
            />
          )}
        </div>
      </PanelContent>
    </Panel>
  );
}

function TrackView({ order }) {
  const timeline = order.timeline || [
    { at: formatDate(order.createdAt), label: "Order Created", actor: "Buyer" },
    ...(order.txHash
      ? [
          {
            at: formatDate(order.createdAt),
            label: "Payment Locked",
            actor: "Escrow",
            tx: order.txHash,
          },
        ]
      : []),
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: "14px", fontWeight: 600 }}>
            Track Timeline
          </div>
          <StatusBadgeComponent
            status={order.order_status || order.Payment_status}
          />
        </div>
        <div
          style={{
            fontSize: "12px",
            color: "rgba(255, 255, 255, 0.7)",
            marginTop: "8px",
          }}
        >
          Tx: {order.txHash ? shortHash(order.txHash) : "—"} • Block:{" "}
          {order.block_number || "—"}
        </div>
      </Card>

      <Card>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {timeline.map((e, idx) => (
            <div key={idx} style={{ display: "flex", gap: "12px" }}>
              <div
                style={{
                  marginTop: "4px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "#00f7d2",
                    boxShadow: "0 0 18px rgba(0, 247, 210, 0.35)",
                  }}
                />
                {idx < timeline.length - 1 && (
                  <span
                    style={{
                      width: "1px",
                      flex: 1,
                      background: "rgba(255, 255, 255, 0.1)",
                    }}
                  />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>
                  {e.label}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  {e.at} • {e.actor}
                </div>
                {e.tx && (
                  <a
                    href={bscscan(e.tx)}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      fontSize: "12px",
                      textDecoration: "none",
                      color: "#e5e7eb",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      marginTop: "4px",
                    }}
                  >
                    {shortHash(e.tx)}{" "}
                    <ExternalLink size={14} style={{ opacity: 0.7 }} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function InvoiceView({
  order,
  onUpdate,
  viewMode,
  onClose,
  onRefetch,
  setIsLoading,
}) {
  const inv = order.invoice || {
    id: order._id,
    method: order.payment_method || "Escrow",
  };
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [nextStatus, setNextStatus] = useState(order.order_status || "waiting");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const fileInputRef = useRef(null);

  // Check if invoice exists (not empty string)
  const hasInvoice = order.invoice && order.invoice.trim() !== "";

  // Only show upload and status actions for seller mode
  const isSellerMode = viewMode === "seller";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inv.hash || inv.id || "");
      toast.success("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleStatusChange = async () => {
    if (nextStatus === order.order_status) {
      toast.info("Status is already set to this value");
      return;
    }

    setIsUpdatingStatus(true);
    if (setIsLoading) {
      setIsLoading(true);
    }
    try {
      await updateOrderStatusSeller({
        order_id: order._id,
        status: nextStatus,
      });
      if (onUpdate) {
        onUpdate(order._id, { order_status: nextStatus });
      }
      toast.success("Order status updated successfully", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } catch (error) {
      console.error("Error updating order status:", error);
      const errorMessage =
        error.response?.data?.Message || "Failed to update order status";
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsUpdatingStatus(false);
      if (setIsLoading) {
        setIsLoading(false);
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setInvoiceFile(file);
    }
  };

  const handleUploadInvoice = async () => {
    if (!invoiceFile) {
      toast.error("Please select an invoice file", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const formData = new FormData();
    formData.append("order_id", order._id);
    formData.append("File", invoiceFile);

    setIsUploading(true);
    try {
      const response = await uploadInvoice(formData);

      if (response.status === 200) {
        setIsUploading(false);
        toast.success("Invoice uploaded successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setInvoiceFile(null);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        // Close sidebar and refetch orders
        if (onClose) {
          onClose();
        }
        if (onRefetch) {
          onRefetch();
        }
      } else {
        setIsUploading(false);
        toast.error("Failed to upload invoice", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      setIsUploading(false);
      console.error("Error uploading invoice:", error);
      const errorMessage =
        error.response?.data?.Message || "Failed to upload invoice";
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const handleViewInvoice = () => {
    if (hasInvoice && order.invoice) {
      window.open(order.invoice, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Card>
        <div style={{ fontSize: "14px", fontWeight: 600 }}>Invoice</div>
        <div
          style={{
            fontSize: "12px",
            color: "rgba(255, 255, 255, 0.7)",
            marginTop: "4px",
          }}
        >
          Invoice ID: {inv.id || order._id} • Method: {inv.method || "Escrow"}{" "}
          {inv.vat ? `• VAT: ${inv.vat}` : ""}
        </div>
      </Card>

      {hasInvoice ? (
        // Show View Invoice button if invoice exists
        <Card style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          <Button primary onClick={handleViewInvoice}>
            <Eye size={16} style={{ marginRight: "6px" }} />
            View Invoice
          </Button>
        </Card>
      ) : (
        // Show Upload Invoice form if invoice doesn't exist (only for seller mode)
        isSellerMode && (
          <Card>
            <div
              style={{
                fontSize: "14px",
                fontWeight: 600,
                marginBottom: "12px",
              }}
            >
              Upload Invoice
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,image/*"
                style={{ display: "none" }}
              />
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  style={{ flexShrink: 0 }}
                >
                  Select File
                </Button>
                {invoiceFile && (
                  <div
                    style={{
                      flex: 1,
                      fontSize: "12px",
                      color: "rgba(255, 255, 255, 0.7)",
                      padding: "8px 12px",
                      background: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "8px",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    {invoiceFile.name}
                  </div>
                )}
              </div>
              {invoiceFile && (
                <Button
                  primary
                  onClick={handleUploadInvoice}
                  disabled={isUploading}
                  style={{
                    marginTop: "4px",
                    opacity: isUploading ? 0.6 : 1,
                    cursor: isUploading ? "not-allowed" : "pointer",
                  }}
                >
                  {isUploading ? "Uploading..." : "Upload Invoice"}
                </Button>
              )}
            </div>
          </Card>
        )
      )}

      {isSellerMode && (
        <Card>
          <div
            style={{ fontSize: "14px", fontWeight: 600, marginBottom: "12px" }}
          >
            Order Status
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "12px",
                    color: "rgba(255, 255, 255, 0.7)",
                    marginBottom: "6px",
                  }}
                >
                  Current Status
                </div>
                <StatusBadgeComponent
                  status={
                    order.order_status || order.Payment_status || "waiting"
                  }
                />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Select
                value={nextStatus}
                onChange={(e) => setNextStatus(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="waiting" style={{ background: "#0d1220" }}>
                  Waiting
                </option>
                <option value="shipped" style={{ background: "#0d1220" }}>
                  Shipped
                </option>
                <option value="delivered" style={{ background: "#0d1220" }}>
                  Delivered
                </option>
                <option value="completed" style={{ background: "#0d1220" }}>
                  Completed
                </option>
              </Select>
              <Button
                primary
                onClick={handleStatusChange}
                disabled={isUpdatingStatus || nextStatus === order.order_status}
                style={{
                  opacity:
                    isUpdatingStatus || nextStatus === order.order_status
                      ? 0.6
                      : 1,
                  cursor:
                    isUpdatingStatus || nextStatus === order.order_status
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {isUpdatingStatus ? "Updating..." : "Update Status"}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function ChatView({ order }) {
  const [text, setText] = useState("");
  const [msgs, setMsgs] = useState(order.chat_messages || []);

  const send = () => {
    const t = text.trim();
    if (!t) return;
    setMsgs((m) => [
      ...m,
      {
        at: new Date().toISOString().slice(0, 16).replace("T", " "),
        from: "You",
        text: t,
      },
    ]);
    setText("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Card>
        <div style={{ fontSize: "14px", fontWeight: 600 }}>Chat with Owner</div>
        <div
          style={{
            fontSize: "12px",
            color: "rgba(255, 255, 255, 0.7)",
            marginTop: "4px",
          }}
        >
          Context: {order._id || order.order_no} • Auto-events on status change
          (ready)
        </div>
      </Card>

      <Card
        style={{
          height: "320px",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {msgs.map((m, idx) => (
          <div
            key={idx}
            style={{
              maxWidth: "85%",
              marginLeft: m.from === "You" ? "auto" : "0",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "rgba(255, 255, 255, 0.7)",
                marginBottom: "4px",
              }}
            >
              {m.from} • {m.at}
            </div>
            <div
              style={{
                borderRadius: "16px",
                padding: "12px",
                border: `1px solid ${m.from === "You" ? "rgba(0, 247, 210, 0.15)" : "rgba(255, 255, 255, 0.1)"}`,
                background:
                  m.from === "You"
                    ? "rgba(0, 247, 210, 0.1)"
                    : "rgba(255, 255, 255, 0.05)",
              }}
            >
              <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
                {m.text}
              </div>
            </div>
          </div>
        ))}
      </Card>

      <Card style={{ display: "flex", gap: "8px" }}>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a message…"
          onKeyPress={(e) => e.key === "Enter" && send()}
        />
        <Button primary onClick={send}>
          Send
        </Button>
      </Card>
    </div>
  );
}

function OrderView({ order, onUpdate, setIsLoading, viewMode }) {
  const copy = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const [nextStatus, setNextStatus] = useState(order.order_status || "Pending");
  const [docKind, setDocKind] = useState("doc");

  const applyStatus = async () => {
    if (nextStatus !== order.order_status) {
      if (setIsLoading) {
        setIsLoading(true);
      }
      try {
        await updateOrderStatusSeller({
          order_id: order._id,
          status: nextStatus,
        });
        onUpdate(order._id, { order_status: nextStatus });
        toast.success("Order status updated");
      } catch (error) {
        toast.error("Failed to update order status");
      } finally {
        if (setIsLoading) {
          setIsLoading(false);
        }
      }
    }
  };

  const onFiles = async (files) => {
    if (!files || !files.length) return;
    try {
      const formData = new FormData();
      formData.append("order_id", order._id);
      Array.from(files).forEach((file) => {
        formData.append("File", file);
      });
      await uploadInvoice(formData);
      toast.success("Invoice uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload invoice");
    }
  };

  const Row = ({ k, v, copyVal }) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "12px",
        padding: "8px 0",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        borderBottomWidth: "1px",
      }}
    >
      <div style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.7)" }}>
        {k}
      </div>
      <div
        style={{
          fontSize: "14px",
          textAlign: "right",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <div style={{ maxWidth: "340px", wordBreak: "break-word" }}>{v}</div>
        {copyVal && (
          <Button
            onClick={() => copy(copyVal)}
            style={{ padding: "6px", minWidth: "auto" }}
            title="Copy"
          >
            <Copy size={14} />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Card>
        <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
          Core
        </div>
        <Row
          k="Order ID"
          v={order._id || order.order_no}
          copyVal={order._id || order.order_no}
        />
        <Row k="Date" v={formatDate(order.createdAt || order.created_at)} />
        <Row
          k="Counterparty"
          v={`${viewMode === "buyer" 
            ? (order.seller_wallet?.[0]?.name || order.seller_wallet?.name || order.seller_info?.name || order.seller_name || order.store_detail?.[0]?.storeName || "Unknown")
            : (order.buyer_info?.name || order.buyer_name || order.buyer_detail?.[0]?.name || "Unknown")} (${viewMode === "buyer" ? "Seller" : "Buyer"})`}
        />
        <Row k="Network" v={order.network || order.symbol || "BNB"} />
        <Row k="Payment Method" v={order.wallet_type === "internal" ? "Virtual Wallet" : (order.wallet_type || "N/A")} />
        <Row k="Order Type" v={order.order_type === "group" ? "Group Order" : "Individual Order"} />
        <Row k="Order Status" v={order.order_status  || "Unknown"} />
        <Row k="Payment Status" v={order?.Payment_status  || "Unknown"} />
        <Row k="KYC" v={order.kyc_status || "N/A"} />
        <Row k="Escrow" v={order.escrow_status || "N/A"} />
      </Card>

      {order.product_details && order.product_details.length > 0 && (
        <Card>
          <div
            style={{ fontSize: "14px", fontWeight: 600, marginBottom: "12px" }}
          >
            Order Items ({order.product_details.length})
          </div>
          {order.product_details.map((product, idx) => {
            // Find matching product object for additional details (title, pictures)
            const productInfo = order.productObject?.find(
              (p) => p._id === product.product_id
            ) || {};
            const productTitle = productInfo.title || product.product_name || product.name || product.title || `Product ${idx + 1}`;
            const productImage = productInfo.pictures?.[0];
            
            return (
              <div 
                key={product._id || idx} 
                style={{ 
                  marginBottom: idx < order.product_details.length - 1 ? "16px" : "0",
                  paddingBottom: idx < order.product_details.length - 1 ? "16px" : "0",
                  borderBottom: idx < order.product_details.length - 1 ? "1px solid rgba(255, 255, 255, 0.1)" : "none"
                }}
              >
                <div style={{ 
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "12px",
                }}>
                  {productImage && (
                    <img 
                      src={productImage} 
                      alt={productTitle}
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        objectFit: "cover",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                      }}
                    />
                  )}
                  <div style={{ 
                    fontSize: "14px", 
                    fontWeight: 500, 
                    color: "#67e8f9"
                  }}>
                    {productTitle}
                  </div>
                </div>
                <Row k="Product ID" v={product.product_id || product._id || "—"} copyVal={product.product_id || product._id} />
                {/* <Row k="Quantity" v={product.quantity || "—"} /> */}
                <Row k="Unit Price" v={product.price ? `${product.price} ${order.symbol || 'USDT'}` : '—'} />
                {/* {product.quantity && product.price && (
                  <Row k="Subtotal" v={`${(product.quantity * product.price).toFixed(2)} ${order.symbol || 'USDT'}`} />
                )} */}
              </div>
            );
          })}
          <div style={{ 
            marginTop: "12px", 
            paddingTop: "12px", 
            borderTop: "1px solid rgba(255, 255, 255, 0.1)" 
          }}>
            <Row k="Total Quantity" v={order.total_quantity || order.product_details.reduce((sum, p) => sum + (p.quantity || 0), 0)} />
          </div>
        </Card>
      )}

      <Card>
        <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
          Amount
        </div>
        <Row
          k="Asset"
          v={
            order.total_amount
              ? `${parseFloat(order.total_amount).toFixed(4)} ${order.symbol || "USDT"}`
              : "—"
          }
        />
        <Row
          k="Fiat (approx.)"
          v={
            order.total_amount
              ? `${parseFloat(order.total_amount).toLocaleString()} ${order.fiat_currency || "EUR"}`
              : "—"
          }
        />
        {order.total_delivery_charges != null && (
          <Row
            k="Shipping"
            v={
              order.total_delivery_charges
                ? `${parseFloat(order.total_delivery_charges).toFixed(4)} ${order.symbol || "USDT"}`
                : "—"
            }
          />
        )}
        {order.vat_tax != null && (
          <Row
            k="VAT"
            v={
              order.vat_tax
                ? `${parseFloat(order.vat_tax).toFixed(4)} ${order.symbol || "USDT"}`
                : "—"
            }
          />
        )}
        {order.sale_tax != null && (
          <Row
            k="Sales Tax"
            v={
              order.sale_tax
                ? `${parseFloat(order.sale_tax).toFixed(4)} ${order.symbol || "USDT"}`
                : "—"
            }
          />
        )}
        <Row
          k="Total Paid"
          v={
            (() => {
              const totalAmount = parseFloat(order.total_amount || 0);
              const shipping = parseFloat(order.total_delivery_charges || 0);
              const vat = parseFloat(order.vat_tax || 0);
              const salesTax = parseFloat(order.sale_tax || 0);
              const totalPaid = totalAmount + shipping + vat + salesTax;
              return totalPaid > 0
                ? `${totalPaid.toFixed(4)} ${order.symbol || "USDT"}`
                : "—";
            })()
          }
        />
      </Card>

      <Card>
        <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
          On-chain Proof
        </div>
        <Row
          k="Tx Hash"
          v={
            <a
              href={bscscan(order.txHash || order.transaction_hash || order.order_transaction_hash )}
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration: "none",
                color: "#e5e7eb",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {/* {order.txHash || order.transaction_hash || order?.order_transaction_hash ||  "—"}{" "} */}
              <ExternalLink size={14} style={{ opacity: 0.7 }} />
            </a>
          }
          copyVal={order.txHash || order.transaction_hash || order?.order_transaction_hash }
        />
        <Row k="Block" v={order.block_number || "—"} />
      </Card>

      <Card>
        <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
          Invoice
        </div>
        <Row
          k="Invoice ID"
          v={order.invoice?.id || order._id}
          copyVal={order.invoice?.id || order._id}
        />
        <Row
          k="Payment method"
          v={order.payment_method || order.invoice?.method || "Escrow"}
        />
        {order.invoice?.vat && <Row k="VAT" v={order.invoice.vat} />}
        {order.invoice?.hash && (
          <Row
            k="Invoice hash"
            v={order.invoice.hash}
            copyVal={order.invoice.hash}
          />
        )}
      </Card>
    </div>
  );
}

export default function GetMyOrders() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("track");
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("seller"); // 'seller', 'buyer', 'approver'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Approver state
  const [showApproveSidebar, setShowApproveSidebar] = useState(false);
  const [showRejectSidebar, setShowRejectSidebar] = useState(false);
  const [selectedOrderForApproval, setSelectedOrderForApproval] =
    useState(null);
  const [isBuyerApprover, setIsBuyerApprover] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [orderSymbol, setOrderSymbol] = useState("");
  const [acceptReason, setAcceptReason] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [approveFile, setApproveFile] = useState(null);
  const [rejectFile, setRejectFile] = useState(null);
  const [approveFile2, setApproveFile2] = useState(null);
  const [signatureImage, setSignatureImage] = useState(null);
  const [signatureImage1, setSignatureImage1] = useState(null);
  const [imageSelfie, setImageSelfie] = useState(null);
  const [selectedApprovalType, setSelectedApprovalType] = useState("approval"); // 'payment' or 'approval'
  const [capturedImage, setCapturedImage] = useState(null);
  const [showWebCam, setShowWebCam] = useState(false);
  const [getSubmit, setGetSubmit] = useState(false);
  const fileSelfieUpload = React.useRef(null);
  const [showOrderDetailSidebar, setShowOrderDetailSidebar] = useState(false);
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState(null);
  const [selectedOfferForDetail, setSelectedOfferForDetail] = useState(null);

  // Custom Offer state
  const [offerId, setOfferId] = useState("");
  const [offerStatus, setOfferStatus] = useState("all");
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const loggedInUser = JSON.parse(localStorage.getItem("user"))?.data?.user;

  // Dropdown state
  const [isViewModeDropdownOpen, setIsViewModeDropdownOpen] = useState(false);

  const fetchOrders = async (page = currentPage) => {
    let payload;

    if (viewMode === "customOffers") {
      // Custom offers use different payload format
      payload = {
        page_number: page,
        limit: 10,
        offer_status: offerStatus,
        offer_id: offerId || undefined,
      };
    } else {
      payload = {
        limit: 10,
        pageNumber: page,
        orderStatus: viewMode === "approver" ? "all" : "all",
        approver_status: viewMode === "approver" ? "all" : undefined,
      };
    }

    setIsLoading(true);
    // Clear previous results so we don't show stale orders when switching modes
    setOrders([]);
    try {
      let response;
      if (viewMode === "buyer") {
        response = await getMyOrdersAsBuyer(payload);
      } else if (viewMode === "approver") {
        response = await getMyApproval(payload);
      } else if (viewMode === "customOffers") {
        response = await getCustomOffers(payload);
      } else {
        response = await getMyOrders(payload);
      }

      let responseData;
      if (viewMode === "customOffers") {
        // Custom offers response structure is different
        responseData = response.data?.data || response.data || [];
        // Convert to array if it's not already
        const offersArray = Array.isArray(responseData) ? responseData : [];
        setOrders(offersArray);

        // Set pagination (custom offers might not have pagination info)
        setTotalPages(offersArray.length === 10 ? page + 1 : page);
        setTotalRecords(offersArray.length);
        setIsLoading(false);
        return;
      }

      responseData = response.data;

      // Extract pagination info from response
      if (responseData.totalPages !== undefined) {
        setTotalPages(responseData.totalPages);
      } else if (
        responseData.total !== undefined &&
        responseData.limit !== undefined
      ) {
        setTotalPages(Math.ceil(responseData.total / responseData.limit));
      } else {
        // Estimate from data length
        setTotalPages(responseData.data?.length === 10 ? page + 1 : page);
      }

      if (responseData.total !== undefined) {
        setTotalRecords(responseData.total);
      } else if (responseData.data?.length) {
        setTotalRecords(responseData.data.length);
      }

      if (responseData.data && responseData.data.length > 0) {
        setOrders(responseData.data);
      } else {
        setOrders([]);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching orders:", error);

      // If buyer/approver/customOffers mode and API returns 404 / no orders, just show empty state message
      const status = error?.response?.status;
      if (
        (viewMode === "buyer" ||
          viewMode === "approver" ||
          viewMode === "customOffers") &&
        (status === 404 || status === 400)
      ) {
        setOrders([]);
        setTotalPages(1);
        setTotalRecords(0);
        return;
      }
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when switching modes
    if (viewMode !== "customOffers") {
      // Reset custom offer filters when switching away
      setOfferId("");
      setOfferStatus("all");
    }
    fetchOrders(1);
  }, [viewMode, offerStatus, offerId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isViewModeDropdownOpen &&
        !event.target.closest("[data-dropdown-container]")
      ) {
        setIsViewModeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isViewModeDropdownOpen]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchOrders(newPage);
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Helper functions for approve/reject
  const base64ToFile = (base64String, filename) => {
    if (!filename || !base64String) return null;
    const parts = base64String.split(";base64,");
    const contentType = parts[0]?.split(":")[1];
    const raw = window.atob(parts[1]);
    const rawLength = raw?.length;
    const uint8Array = new Uint8Array(rawLength);
    for (let i = 0; i < rawLength; ++i) {
      uint8Array[i] = raw?.charCodeAt(i);
    }
    const blob = new Blob([uint8Array], { type: contentType });
    return new File([blob], filename, { type: contentType });
  };

  const dataURLtoBlob = (dataUrl) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleSignatureEnd = (dataUrl) => {
    const blob = dataURLtoBlob(dataUrl);
    const fileName = "signature.png";
    const fileType = "image/png";
    const file = new File([blob], fileName, { type: fileType });
    setSignatureImage(dataUrl);
    setSignatureImage1(file);
  };

  const handleFileChangeApprove = (e) => {
    const selectedFile = e.target.files[0];
    setApproveFile(selectedFile);
  };

  const handleFileChangeReject = (e) => {
    const selectedFile = e.target.files[0];
    setRejectFile(selectedFile);
  };

  const handleSelfieUpload = (e) => {
    const selectedImage = e.target.files[0];
    setImageSelfie(selectedImage);
  };

  const handleCaptureImage = (imageSrc) => {
    setCapturedImage(imageSrc);
    setShowWebCam(false);
  };

  const getPDf = (value) => {
    setGetSubmit(true);
    setApproveFile2(value);
  };

  const getPDf1 = () => {
    setGetSubmit(false);
  };

  const openApproveSidebar = (order, type = "approval") => {
    setSelectedOrderForApproval(order);
    setIsBuyerApprover(order?.buyer_is_approved_status === "pending");
    setIsApprover(order?.is_approved_status === "pending");
    setOrderSymbol(order?.symbol || "");
    setSelectedApprovalType(type); // 'payment' or 'approval'
    setShowApproveSidebar(true);
  };

  const openRejectSidebar = (order, type = "approval") => {
    setSelectedOrderForApproval(order);
    setIsBuyerApprover(order?.buyer_is_approved_status === "pending");
    setIsApprover(order?.is_approved_status === "pending");
    setOrderSymbol(order?.symbol || "");
    setSelectedApprovalType(type); // 'payment' or 'approval'
    setShowRejectSidebar(true);
  };

  const closeApproveSidebar = () => {
    setShowApproveSidebar(false);
    setSelectedOrderForApproval(null);
    setAcceptReason("");
    setApproveFile(null);
    setApproveFile2(null);
    setSignatureImage(null);
    setSignatureImage1(null);
    setImageSelfie(null);
    setCapturedImage(null);
    setGetSubmit(false);
  };

  const closeRejectSidebar = () => {
    setShowRejectSidebar(false);
    setSelectedOrderForApproval(null);
    setRejectionReason("");
    setRejectFile(null);
    setSignatureImage(null);
    setSignatureImage1(null);
    setImageSelfie(null);
    setCapturedImage(null);
    setGetSubmit(false);
  };

  const handleApprove = async () => {
    const capturedImageFile = base64ToFile(capturedImage, "captured_image.png");
    const formData = new FormData();
    formData.append("order_id", selectedOrderForApproval._id);
    formData.append("approver_reason", acceptReason);
    formData.append("File", capturedImageFile || imageSelfie);
    formData.append("File2", approveFile2);

    if (!acceptReason && !approveFile2) {
      toast.error("Approve reason & document is required");
      return;
    }
    if (!capturedImageFile && !imageSelfie) {
      toast.error("e-signed document is required");
      return;
    }
    if (!acceptReason) {
      toast.error("Approve reason is required");
      return;
    }
    if (!approveFile2) {
      toast.error("Approve document is required");
      return;
    }

    setIsLoading(true);
    try {
      let response;
      if (isBuyerApprover) {
        response =
          orderSymbol === "EURO"
            ? await buyerApproveOrderBank(formData)
            : await buyerApproveOrder(formData);
      } else if (isApprover) {
        response =
          orderSymbol === "EURO"
            ? await approveOrderBank(formData)
            : await approveOrder(formData);
      }

      if (response.status === 200) {
        toast.success("Order approved successfully");
        closeApproveSidebar();
        fetchOrders(currentPage);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error approving order:", error);
      toast.error(error.response?.data?.Message || "Error approving order");
    }
  };

  const handleReject = async () => {
    const capturedImageFile = base64ToFile(capturedImage, "captured_image.png");
    const formData = new FormData();
    formData.append("order_id", selectedOrderForApproval._id);
    formData.append("rejection_reason", rejectionReason);
    formData.append("File", capturedImageFile || imageSelfie);
    formData.append("File2", approveFile2);

    if (!rejectionReason && !approveFile2) {
      toast.error("Rejection reason & document is required");
      return;
    }
    if (!capturedImage && !imageSelfie) {
      toast.error("e-signed document is required");
      return;
    }
    if (!rejectionReason) {
      toast.error("Rejection reason is required");
      return;
    }
    if (!approveFile2) {
      toast.error("Rejection document is required");
      return;
    }

    setIsLoading(true);
    try {
      let responseReject;
      if (isBuyerApprover) {
        responseReject =
          orderSymbol === "EURO"
            ? await buyeRejectOrderBank(formData)
            : await buyerRejectOrder(formData);
      } else if (isApprover) {
        responseReject =
          orderSymbol === "EURO"
            ? await rejectOrderBank(formData)
            : await rejectOrder(formData);
      }

      if (responseReject.status === 200) {
        toast.success(
          "Order is successfully rejected and system refund the payment"
        );
        closeRejectSidebar();
        fetchOrders(currentPage);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error rejecting order:", error);
      toast.error(error.response?.data?.Message || "Error rejecting order");
    }
  };

  const filteredOrders = useMemo(() => {
    if (viewMode === "customOffers") {
      // Custom offers are already filtered by API (offerId and offerStatus)
      return orders;
    }
    const s = q.trim().toLowerCase();
    return orders
      .filter((o) =>
        status === "All"
          ? true
          : (o.order_status || "").toLowerCase() === status.toLowerCase()
      )
      .filter((o) =>
        !s
          ? true
          : [
              o._id,
              o.order_no,
              o.buyer_info?.name,
              o.buyer_name,
              o.txHash,
              o.transaction_hash,
            ].some((x) => x && x.toString().toLowerCase().includes(s))
      );
  }, [q, status, orders, viewMode]);

  const metrics = useMemo(() => {
    if (viewMode === "customOffers") {
      // Custom offers don't have the same status structure
      const total = orders.length;
      const active = orders.filter((o) => o.offer_status === "active").length;
      const rejected = orders.filter((o) => o.offer_status === "reject").length;
      return { total, inProgress: active, completed: 0, disputed: rejected };
    }
    const total = orders.length;
    const inProgress = orders.filter((o) =>
      ["Pending", "Paid", "In Escrow", "Shipped"].includes(o.order_status)
    ).length;
    const completed = orders.filter(
      (o) => o.order_status === "Completed"
    ).length;
    const disputed = orders.filter((o) => o.order_status === "Disputed").length;
    return { total, inProgress, completed, disputed };
  }, [orders, viewMode]);

  // Custom Offer handlers
  const handleAcceptCustomOffer = async (item) => {
    setIsLoading(true);
    try {
      const res = await acceptCustomOffer({ offer_id: item?._id });
      toast.success(res?.data?.Message || "Custom offer accepted successfully");
      fetchOrders(currentPage);
    } catch (error) {
      setIsLoading(false);
      toast.error(
        error?.response?.data?.Message || "Error accepting custom offer"
      );
    }
  };

  const handleRejectCustomOffer = async (item) => {
    setIsLoading(true);
    try {
      const res = await rejectCustomOffer({ offer_id: item?._id });
      toast.success(res?.data?.Message || "Custom offer rejected successfully");
      fetchOrders(currentPage);
    } catch (error) {
      setIsLoading(false);
      toast.error(
        error?.response?.data?.Message || "Error rejecting custom offer"
      );
    }
  };

  const onOpen = (m, o) => {
    setMode(m);
    const fresh = orders.find((x) => x._id === o._id) || o;
    setSelected(fresh);
    setOpen(true);
  };

  const onUpdate = (id, patch) => {
    setOrders((prev) =>
      prev.map((o) => (o._id === id ? { ...o, ...patch } : o))
    );
    setSelected((cur) => (cur && cur._id === id ? { ...cur, ...patch } : cur));
  };

  return (
    <PageContainer>
      <div style={{ maxWidth: "1536px", margin: "0 auto", padding: "0 16px" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: "16px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "24px",
                fontWeight: 600,
                letterSpacing: "-0.025em",
                marginBottom: "4px",
              }}
            >
              Order Management
            </div>
            <div
              style={{ fontSize: "14px", color: "rgba(255, 255, 255, 0.7)" }}
            >
              Marketplace orders • Action Layer • On-chain proof
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <Chip>Total {metrics.total}</Chip>
            <Chip>In Progress {metrics.inProgress}</Chip>
            <Chip>Completed {metrics.completed}</Chip>
            <Chip>Disputed {metrics.disputed}</Chip>
          </div>
        </div>

        <Card style={{ marginBottom: "16px", padding: "12px" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: "300px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                borderRadius: "12px",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                background: "rgba(255, 255, 255, 0.05)",
              }}
            >
              <Search size={16} style={{ opacity: 0.8, flexShrink: 0 }} />
              <Input
                value={viewMode === "customOffers" ? offerId : q}
                onChange={(e) => {
                  if (viewMode === "customOffers") {
                    setOfferId(e.target.value);
                  } else {
                    setQ(e.target.value);
                  }
                }}
                placeholder={
                  viewMode === "customOffers"
                    ? "Search by Offer ID"
                    : "Search by Order ID / Hash / Counterparty"
                }
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  width: "100%",
                }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <DropdownContainer data-dropdown-container>
                <DropdownButton
                  active={true}
                  onClick={() =>
                    setIsViewModeDropdownOpen(!isViewModeDropdownOpen)
                  }
                >
                  {viewMode === "seller" && "As Seller"}
                  {viewMode === "buyer" && "As Buyer"}
                  {viewMode === "approver" && "By Approver"}
                  {viewMode === "customOffers" && "By Custom Offer"}
                  <ChevronDown size={16} style={{ marginLeft: "auto" }} />
                </DropdownButton>
                <DropdownMenu open={isViewModeDropdownOpen}>
                  <DropdownItem
                    active={viewMode === "seller"}
                    onClick={() => {
                      setViewMode("seller");
                      setIsViewModeDropdownOpen(false);
                    }}
                  >
                    As Seller
                  </DropdownItem>
                  <DropdownItem
                    active={viewMode === "buyer"}
                    onClick={() => {
                      setViewMode("buyer");
                      setIsViewModeDropdownOpen(false);
                    }}
                  >
                    As Buyer
                  </DropdownItem>
                  <DropdownItem
                    active={viewMode === "approver"}
                    onClick={() => {
                      setViewMode("approver");
                      setIsViewModeDropdownOpen(false);
                    }}
                  >
                    By Approver
                  </DropdownItem>
                  <DropdownItem
                    active={viewMode === "customOffers"}
                    onClick={() => {
                      setViewMode("customOffers");
                      setIsViewModeDropdownOpen(false);
                    }}
                  >
                    By Custom Offer
                  </DropdownItem>
                </DropdownMenu>
              </DropdownContainer>
              {viewMode === "customOffers" ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    background: "rgba(255, 255, 255, 0.05)",
                  }}
                >
                  <Filter size={16} style={{ opacity: 0.8, flexShrink: 0 }} />
                  <Select
                    value={offerStatus}
                    onChange={(e) => setOfferStatus(e.target.value)}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      cursor: "pointer",
                      color: "#e5e7eb",
                    }}
                  >
                    <option value="all" style={{ background: "#0d1220" }}>
                      All
                    </option>
                    <option value="active" style={{ background: "#0d1220" }}>
                      Active
                    </option>
                    <option value="reject" style={{ background: "#0d1220" }}>
                      Rejected
                    </option>
                  </Select>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    background: "rgba(255, 255, 255, 0.05)",
                  }}
                >
                  <Filter size={16} style={{ opacity: 0.8, flexShrink: 0 }} />
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      cursor: "pointer",
                      color: "#e5e7eb",
                    }}
                  >
                    <option value="All" style={{ background: "#0d1220" }}>
                      All
                    </option>
                    <option value="Pending" style={{ background: "#0d1220" }}>
                      Pending
                    </option>
                    <option value="Paid" style={{ background: "#0d1220" }}>
                      Paid
                    </option>
                    <option value="In Escrow" style={{ background: "#0d1220" }}>
                      In Escrow
                    </option>
                    <option value="Shipped" style={{ background: "#0d1220" }}>
                      Shipped
                    </option>
                    <option value="Completed" style={{ background: "#0d1220" }}>
                      Completed
                    </option>
                    <option value="Disputed" style={{ background: "#0d1220" }}>
                      Disputed
                    </option>
                  </Select>
                </div>
              )}
              <Chip>Compact PRO</Chip>
            </div>
          </div>
        </Card>

        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            <Ring size={40} color="#00f7d2" />
          </div>
        ) : viewMode === "approver" ? (
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <ApproverTable>
              <ApproverTableHeader>
                <ApproverTableHeaderRow>
                  <ApproverTableHeaderCell>Order No.</ApproverTableHeaderCell>
                  <ApproverTableHeaderCell>Quantity</ApproverTableHeaderCell>
                  <ApproverTableHeaderCell>Total</ApproverTableHeaderCell>
                  <ApproverTableHeaderCell>Payment</ApproverTableHeaderCell>
                  <ApproverTableHeaderCell>Approval</ApproverTableHeaderCell>
                  <ApproverTableHeaderCell>
                    Buyer Approval
                  </ApproverTableHeaderCell>
                  <ApproverTableHeaderCell>Order</ApproverTableHeaderCell>
                  <ApproverTableHeaderCell>Action</ApproverTableHeaderCell>
                </ApproverTableHeaderRow>
              </ApproverTableHeader>
              <ApproverTableBody>
                {filteredOrders.map((o) => (
                  <ApproverTableRow
                    key={o._id || o.order_no}
                    order={o}
                    onOpen={onOpen}
                    onApprovePayment={(order) =>
                      openApproveSidebar(order, "payment")
                    }
                    onRejectPayment={(order) =>
                      openRejectSidebar(order, "payment")
                    }
                    onApproveOrder={(order) =>
                      openApproveSidebar(order, "approval")
                    }
                    onRejectOrder={(order) =>
                      openRejectSidebar(order, "approval")
                    }
                    onViewOrder={(order) => {
                      setSelectedOrderForDetail(order);
                      setSelectedOfferForDetail(null);
                      setShowOrderDetailSidebar(true);
                    }}
                  />
                ))}
              </ApproverTableBody>
            </ApproverTable>
            {!filteredOrders.length && (
              <Card
                style={{
                  padding: "32px",
                  textAlign: "center",
                  color: "rgba(255, 255, 255, 0.7)",
                }}
              >
                No orders found for approval.
              </Card>
            )}
          </Card>
        ) : viewMode === "customOffers" ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {filteredOrders.map((offer) => (
              <CustomOfferCard
                key={offer._id}
                offer={offer}
                onAccept={handleAcceptCustomOffer}
                onReject={handleRejectCustomOffer}
                onView={(offer) => {
                  setSelectedOfferForDetail(offer);
                  setSelectedOrderForDetail(null);
                  setShowOrderDetailSidebar(true);
                }}
                loggedInUser={loggedInUser}
              />
            ))}
            {!filteredOrders.length && (
              <Card
                style={{
                  padding: "32px",
                  textAlign: "center",
                  color: "rgba(255, 255, 255, 0.7)",
                }}
              >
                No custom offers found.
              </Card>
            )}
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {filteredOrders.map((o) => (
              <OrderRow
                key={o._id || o.order_no}
                order={o}
                onOpen={onOpen}
                isActive={
                  open &&
                  selected &&
                  (selected._id === o._id || selected.order_no === o.order_no)
                }
                activeMode={mode}
                viewMode={viewMode}
                onApprove={openApproveSidebar}
                onReject={openRejectSidebar}
              />
            ))}
            {!filteredOrders.length && (
              <Card
                style={{
                  padding: "32px",
                  textAlign: "center",
                  color: "rgba(255, 255, 255, 0.7)",
                }}
              >
                {viewMode === "buyer"
                  ? "No orders found as buyer."
                  : "No orders found."}
              </Card>
            )}
          </div>
        )}

        {!isLoading && filteredOrders.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalRecords}
            itemsPerPage={10}
            onPageChange={handlePageChange}
          />
        )}

        <Card
          style={{
            marginTop: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <div>
            <div style={{ fontSize: "14px", fontWeight: 600 }}>
              Quick Insights
            </div>
            <div
              style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.7)" }}
            >
              Volume • Escrow • Completion time • Risk ready
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <Chip>Volume (mock) €168,400</Chip>
            <Chip>Escrow locked (mock) €144,600</Chip>
            <Chip>Avg completion (mock) 2.1d</Chip>
            <Chip>Risk: 1 flagged</Chip>
          </div>
        </Card>
      </div>

      <ActionPanel
        open={open}
        mode={mode}
        order={selected}
        onClose={() => setOpen(false)}
        onUpdate={onUpdate}
        viewMode={viewMode}
        onRefetch={() => fetchOrders(currentPage)}
        setIsLoading={setIsLoading}
      />

      {/* Approve Sidebar */}
      <SidebarOverlay show={showApproveSidebar} onClick={closeApproveSidebar} />
      <ApproveSidebar open={showApproveSidebar}>
        <SidebarHeader>
          <SidebarTitle>
            {selectedApprovalType === "payment"
              ? "Approve Payment"
              : "Approve Order"}
          </SidebarTitle>
          <SidebarCloseButton onClick={closeApproveSidebar}>
            ×
          </SidebarCloseButton>
        </SidebarHeader>

        {selectedOrderForApproval && (
          <>
            <SidebarFormGroup>
              <SidebarLabel>Enter Reason</SidebarLabel>
              <SidebarTextArea
                value={acceptReason}
                onChange={(e) => setAcceptReason(e.target.value)}
                placeholder="Enter Approve Reason"
                rows={3}
              />
            </SidebarFormGroup>

            <SidebarFormGroup>
              <SidebarLabel>Approve Document (PDF)</SidebarLabel>
              <SidebarFileInput
                type="file"
                accept=".pdf"
                onChange={handleFileChangeApprove}
              />
            </SidebarFormGroup>

            {imageSelfie && !capturedImage && (
              <div style={{ marginBottom: "16px", textAlign: "center" }}>
                <button
                  onClick={() => setImageSelfie(null)}
                  style={{
                    padding: "4px 8px",
                    background: "rgba(239, 68, 68, 0.2)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "6px",
                    color: "#ef4444",
                    cursor: "pointer",
                    marginBottom: "8px",
                  }}
                >
                  Remove
                </button>
                <img
                  src={URL.createObjectURL(imageSelfie)}
                  alt="selfie"
                  style={{
                    maxWidth: "100%",
                    borderRadius: "8px",
                    border: "1px solid #1f2937",
                  }}
                />
              </div>
            )}

            {capturedImage && (
              <div style={{ marginBottom: "16px", textAlign: "center" }}>
                <button
                  onClick={() => setCapturedImage(null)}
                  style={{
                    padding: "4px 8px",
                    background: "rgba(239, 68, 68, 0.2)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "6px",
                    color: "#ef4444",
                    cursor: "pointer",
                    marginBottom: "8px",
                  }}
                >
                  Remove
                </button>
                <img
                  src={capturedImage}
                  alt="captured"
                  style={{
                    maxWidth: "100%",
                    borderRadius: "8px",
                    border: "1px solid #1f2937",
                  }}
                />
              </div>
            )}

            {approveFile && (
              <SidebarFormGroup>
                <SidebarLabel>Signature</SidebarLabel>
                <div
                  style={{
                    border: "2px solid #1f2937",
                    borderRadius: "6px",
                    padding: "12px",
                  }}
                >
                  <SignaturePad onEnd={handleSignatureEnd} getPDf1={getPDf1} />
                  {signatureImage1 && (
                    <PdfSignature
                      pdfFile={approveFile}
                      signatureImage={signatureImage1}
                      getPDf={getPDf}
                    />
                  )}
                </div>
              </SidebarFormGroup>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              <Button
                onClick={() => setShowWebCam(true)}
                style={{
                  padding: "10px",
                  background: "rgba(34, 197, 94, 0.15)",
                  border: "1px solid rgba(34, 197, 94, 0.25)",
                  borderRadius: "10px",
                  color: "#22c55e",
                  cursor: "pointer",
                  disabled: !!imageSelfie,
                }}
                disabled={!!imageSelfie}
              >
                Verify Selfie
              </Button>

              <input
                type="file"
                ref={fileSelfieUpload}
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleSelfieUpload}
              />
              <Button
                onClick={() => fileSelfieUpload.current?.click()}
                style={{
                  padding: "10px",
                  background: "rgba(34, 197, 94, 0.15)",
                  border: "1px solid rgba(34, 197, 94, 0.25)",
                  borderRadius: "10px",
                  color: "#22c55e",
                  cursor: "pointer",
                  disabled: !!capturedImage,
                }}
                disabled={!!capturedImage}
              >
                Upload Selfie
              </Button>
            </div>

            {getSubmit && (
              <SidebarSubmitButton
                onClick={handleApprove}
                disabled={!capturedImage && !imageSelfie}
              >
                Submit Approval
              </SidebarSubmitButton>
            )}
          </>
        )}
      </ApproveSidebar>

      {/* Reject Sidebar */}
      <SidebarOverlay show={showRejectSidebar} onClick={closeRejectSidebar} />
      <RejectSidebar open={showRejectSidebar}>
        <SidebarHeader>
          <SidebarTitle>
            {selectedApprovalType === "payment"
              ? "Reject Payment"
              : "Reject Order"}
          </SidebarTitle>
          <SidebarCloseButton onClick={closeRejectSidebar}>
            ×
          </SidebarCloseButton>
        </SidebarHeader>

        {selectedOrderForApproval && (
          <>
            <SidebarFormGroup>
              <SidebarLabel>Enter Reason</SidebarLabel>
              <SidebarTextArea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter Rejection Reason"
                rows={3}
              />
            </SidebarFormGroup>

            <SidebarFormGroup>
              <SidebarLabel>Rejection Document (PDF)</SidebarLabel>
              <SidebarFileInput
                type="file"
                accept=".pdf"
                onChange={handleFileChangeReject}
              />
            </SidebarFormGroup>

            {imageSelfie && !capturedImage && (
              <div style={{ marginBottom: "16px", textAlign: "center" }}>
                <button
                  onClick={() => setImageSelfie(null)}
                  style={{
                    padding: "4px 8px",
                    background: "rgba(239, 68, 68, 0.2)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "6px",
                    color: "#ef4444",
                    cursor: "pointer",
                    marginBottom: "8px",
                  }}
                >
                  Remove
                </button>
                <img
                  src={URL.createObjectURL(imageSelfie)}
                  alt="selfie"
                  style={{
                    maxWidth: "100%",
                    borderRadius: "8px",
                    border: "1px solid #1f2937",
                  }}
                />
              </div>
            )}

            {capturedImage && (
              <div style={{ marginBottom: "16px", textAlign: "center" }}>
                <button
                  onClick={() => setCapturedImage(null)}
                  style={{
                    padding: "4px 8px",
                    background: "rgba(239, 68, 68, 0.2)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "6px",
                    color: "#ef4444",
                    cursor: "pointer",
                    marginBottom: "8px",
                  }}
                >
                  Remove
                </button>
                <img
                  src={capturedImage}
                  alt="captured"
                  style={{
                    maxWidth: "100%",
                    borderRadius: "8px",
                    border: "1px solid #1f2937",
                  }}
                />
              </div>
            )}

            {rejectFile && (
              <SidebarFormGroup>
                <SidebarLabel>Signature</SidebarLabel>
                <div
                  style={{
                    border: "2px solid #1f2937",
                    borderRadius: "6px",
                    padding: "12px",
                  }}
                >
                  <SignaturePad onEnd={handleSignatureEnd} getPDf1={getPDf1} />
                  {signatureImage1 && (
                    <PdfSignature
                      pdfFile={rejectFile}
                      signatureImage={signatureImage1}
                      getPDf={getPDf}
                    />
                  )}
                </div>
              </SidebarFormGroup>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              <Button
                onClick={() => setShowWebCam(true)}
                style={{
                  padding: "10px",
                  background: "rgba(34, 197, 94, 0.15)",
                  border: "1px solid rgba(34, 197, 94, 0.25)",
                  borderRadius: "10px",
                  color: "#22c55e",
                  cursor: "pointer",
                  disabled: !!imageSelfie,
                }}
                disabled={!!imageSelfie}
              >
                Verify Selfie
              </Button>

              <input
                type="file"
                ref={fileSelfieUpload}
                style={{ display: "none" }}
                accept="image/*"
                onChange={handleSelfieUpload}
              />
              <Button
                onClick={() => fileSelfieUpload.current?.click()}
                style={{
                  padding: "10px",
                  background: "rgba(34, 197, 94, 0.15)",
                  border: "1px solid rgba(34, 197, 94, 0.25)",
                  borderRadius: "10px",
                  color: "#22c55e",
                  cursor: "pointer",
                  disabled: !!capturedImage,
                }}
                disabled={!!capturedImage}
              >
                Upload Selfie
              </Button>
            </div>

            {getSubmit && (
              <SidebarSubmitButton
                onClick={handleReject}
                disabled={!capturedImage && !imageSelfie}
              >
                Submit Rejection
              </SidebarSubmitButton>
            )}
          </>
        )}
      </RejectSidebar>

      {/* Order Detail Sidebar */}
      <SidebarOverlay
        show={showOrderDetailSidebar}
        onClick={() => {
          setShowOrderDetailSidebar(false);
          setSelectedOfferForDetail(null);
        }}
      />
      <OrderDetailSidebar open={showOrderDetailSidebar}>
        <SidebarHeader>
          <SidebarTitle>
            {selectedOfferForDetail
              ? `Offer: ${selectedOfferForDetail._id || "—"}`
              : `Order No: ${selectedOrderForDetail?._id || selectedOrderForDetail?.order_no || "—"}`}
          </SidebarTitle>
          <SidebarCloseButton
            onClick={() => {
              setShowOrderDetailSidebar(false);
              setSelectedOfferForDetail(null);
            }}
          >
            ×
          </SidebarCloseButton>
        </SidebarHeader>

        {selectedOfferForDetail && (() => {
          const offer = selectedOfferForDetail;
          const copyToClipboard = async (v) => {
            try {
              await navigator.clipboard.writeText(String(v));
              toast.success("Copied to clipboard");
            } catch (err) {
              console.error("Failed to copy:", err);
            }
          };
          const sellerName =
            offer.seller_wallet?.[0]?.name ||
            offer.seller_wallet?.name ||
            offer.seller_info?.name ||
            offer.seller_name ||
            "Unknown";
          const buyerName =
            offer.buyer_info?.name ||
            offer.buyer_name ||
            offer.buyer_detail?.[0]?.name ||
            "Unknown";
          const SidebarRow = ({ k, v, copyVal }) => (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "12px",
                padding: "8px 0",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <div style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.7)" }}>{k}</div>
              <div style={{ fontSize: "14px", textAlign: "right", display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ maxWidth: "340px", wordBreak: "break-word" }}>{v}</div>
                {copyVal != null && copyVal !== "" && (
                  <Button onClick={() => copyToClipboard(copyVal)} style={{ padding: "6px", minWidth: "auto" }} title="Copy">
                    <Copy size={14} />
                  </Button>
                )}
              </div>
            </div>
          );
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                overflowY: "auto",
                flex: 1,
                minHeight: 0,
              }}
            >
              <Card>
                <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>Core</div>
                <SidebarRow k="Offer ID" v={offer._id || "—"} copyVal={offer._id} />
                <SidebarRow k="Date" v={formatDate(offer.createdAt || offer.created_at || offer.created_date)} />
                <SidebarRow k="Counterparty" v={`${sellerName} (Seller)`} />
                <SidebarRow k="Network" v={offer.network_name || offer.network || offer.symbol || "—"} />
                <SidebarRow k="Payment Method" v="—" />
                <SidebarRow k="Order Type" v="Custom Offer" />
                <SidebarRow k="Order Status" v={offer.offer_status || "—"} />
                <SidebarRow k="Payment Status" v="—" />
                <SidebarRow k="KYC" v={offer.kyc_status || "N/A"} />
                <SidebarRow k="Escrow" v={offer.escrow_status || "N/A"} />
              </Card>

              <Card>
                <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "12px" }}>
                  Order Items ({offer.product_details?.length || offer.product_object?.length || 1})
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  {offer.product_object?.[0]?.pictures?.[0] && (
                    <img
                      src={offer.product_object[0].pictures[0]}
                      alt={offer.product_object[0]?.title || "Product"}
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 8,
                        objectFit: "cover",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                      }}
                    />
                  )}
                  <div style={{ fontSize: "14px", fontWeight: 500, color: "#67e8f9" }}>
                    {offer.product_object?.[0]?.title || "Custom offer"}
                  </div>
                </div>
                <SidebarRow k="Product name" v={offer.product_object?.[0]?.title || "Custom offer"} />
                <SidebarRow
                  k="Unit Price"
                  v={
                    offer.custom_offer_amount
                      ? `${parseFloat(offer.custom_offer_amount).toFixed(4)} ${offer.symbol || "USDT"}`
                      : "—"
                  }
                />
              </Card>

              <Card>
                <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>Amount</div>
                <SidebarRow
                  k="Custom offer amount"
                  v={
                    offer.custom_offer_amount != null
                      ? `${parseFloat(offer.custom_offer_amount).toFixed(4)} ${offer.symbol || "USDT"}`
                      : "—"
                  }
                />
                <SidebarRow
                  k="Original price"
                  v={
                    offer.orignal_price != null
                      ? `${parseFloat(offer.orignal_price).toFixed(4)} ${offer.symbol || "USDT"}`
                      : "—"
                  }
                />
                <SidebarRow
                  k="Sale tax"
                  v={
                    offer.sale_tax != null
                      ? `${parseFloat(offer.sale_tax).toFixed(4)} ${offer.symbol || "USDT"}`
                      : "—"
                  }
                />
                <SidebarRow
                  k="Total delivery charges"
                  v={
                    offer.total_delivery_charges != null
                      ? `${parseFloat(offer.total_delivery_charges).toFixed(4)} ${offer.symbol || "USDT"}`
                      : "—"
                  }
                />
              </Card>
            </div>
          );
        })()}

        {selectedOrderForDetail && !selectedOfferForDetail && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Order Detail */}
            <SidebarFormGroup>
              <SidebarLabel
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  marginBottom: "12px",
                  color: "#e5e7eb",
                }}
              >
                Order Detail
              </SidebarLabel>
              <Card
                style={{
                  padding: "16px",
                  border: "1px solid #1f2937",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "16px",
                  }}
                >
                  <DetailField
                    label="Quantity"
                    value={
                      selectedOrderForDetail.product_details?.length ||
                      selectedOrderForDetail.quantity
                    }
                  />
                  <DetailField
                    label="Phone Number"
                    value={selectedOrderForDetail.phone_number}
                  />
                  <DetailField
                    label="Shipping"
                    value={`${selectedOrderForDetail.total_delivery_charges || 0} ${selectedOrderForDetail.symbol || "USDT"} (${selectedOrderForDetail.network || "BEP20"})`}
                  />
                  <DetailField
                    label="Secondary Address"
                    value={selectedOrderForDetail.second_delivery_address}
                  />
                  <DetailField
                    label="Grand Total"
                    value={`${selectedOrderForDetail.total_amount || 0} ${selectedOrderForDetail.symbol || "USDT"} (${selectedOrderForDetail.network || "BEP20"})`}
                  />
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "rgba(255, 255, 255, 0.7)",
                        marginBottom: "8px",
                      }}
                    >
                      Payment Hash
                    </div>
                    {selectedOrderForDetail.payment_transaction_hash ? (
                      <a
                        href={getBSCTxUrl(
                          selectedOrderForDetail.payment_transaction_hash
                        )}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            background: "rgba(168, 85, 247, 0.15)",
                            border: "1px solid rgba(168, 85, 247, 0.3)",
                            color: "#a855f7",
                          }}
                        >
                          View
                        </Button>
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                  <div style={{ textAlign: "center", gridColumn: "1 / -1" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "rgba(255, 255, 255, 0.7)",
                        marginBottom: "8px",
                      }}
                    >
                      Delivery Address
                    </div>
                    <div style={{ fontSize: "14px", color: "#e5e7eb" }}>
                      {selectedOrderForDetail.delivery_address || "—"}
                    </div>
                  </div>
                </div>
              </Card>
            </SidebarFormGroup>

            {/* Buyer Detail */}
            <DetailSection
              title="Buyer Detail"
              fields={[
                {
                  label: "Name",
                  value:
                    selectedOrderForDetail.buyer_detail?.[0]?.name ||
                    selectedOrderForDetail.buyer_info?.name ||
                    selectedOrderForDetail.buyer_name,
                  icon: Clock,
                },
                {
                  label: "Email",
                  value:
                    selectedOrderForDetail.buyer_detail?.[0]?.email ||
                    selectedOrderForDetail.buyer_info?.email,
                  icon: ReceiptText,
                },
              ]}
            />

            {/* Store Detail */}
            <DetailSection
              title="Store Detail"
              showCondition={!!selectedOrderForDetail.store_detail?.[0]}
              fields={[
                {
                  label: "Name",
                  value: selectedOrderForDetail.store_detail?.[0]?.storeName,
                  icon: Clock,
                },
                {
                  label: "Address",
                  value: selectedOrderForDetail.store_detail?.[0]?.storeAddress,
                  icon: MapPin,
                },
              ]}
            />

            {/* Store Approver Detail */}
            <DetailSection
              title="Store Approver Detail"
              showCondition={!!selectedOrderForDetail.approver_detail?.[0]}
              fields={[
                {
                  label: "Name",
                  value: selectedOrderForDetail.approver_detail?.[0]?.name,
                  icon: Clock,
                },
                {
                  label: "Email",
                  value: selectedOrderForDetail.approver_detail?.[0]?.email,
                  icon: ReceiptText,
                },
              ]}
            />

            {/* Seller Detail - support seller_wallet as array or single object */}
            {(selectedOrderForDetail.seller_wallet?.[0] || selectedOrderForDetail.seller_wallet?.name) && (() => {
              const seller = Array.isArray(selectedOrderForDetail.seller_wallet)
                ? selectedOrderForDetail.seller_wallet[0]
                : selectedOrderForDetail.seller_wallet;
              return (
              <SidebarFormGroup>
                <SidebarLabel
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    marginBottom: "12px",
                    color: "#e5e7eb",
                  }}
                >
                  Seller Detail
                </SidebarLabel>
                <Card
                  style={{
                    padding: "16px",
                    border: "1px solid #1f2937",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: "16px",
                    }}
                  >
                    <DetailField
                      label="Name"
                      value={seller?.name}
                      icon={Clock}
                    />
                    <DetailField
                      label="Seller ID"
                      value={seller?._id}
                      icon={ReceiptText}
                    />
                    <DetailField
                      label="Seller Amount"
                      value={`${selectedOrderForDetail.seller_total_amount || 0} ${selectedOrderForDetail.symbol || "USDT"}`}
                    />
                    <DetailField
                      label="Agency %"
                      value={selectedOrderForDetail.agency_percentage || 0}
                    />
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "rgba(255, 255, 255, 0.7)",
                          marginBottom: "8px",
                        }}
                      >
                        Payment Hash
                      </div>
                      {selectedOrderForDetail.payment_transaction_hash_agency ? (
                        <a
                          href={getBSCTxUrl(
                            selectedOrderForDetail.payment_transaction_hash_agency
                          )}
                          target="_blank"
                          rel="noreferrer"
                          style={{ textDecoration: "none" }}
                        >
                          <Button
                            style={{
                              padding: "6px 12px",
                              fontSize: "12px",
                              background: "rgba(168, 85, 247, 0.15)",
                              border: "1px solid rgba(168, 85, 247, 0.3)",
                              color: "#a855f7",
                            }}
                          >
                            View
                          </Button>
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                    <DetailField
                      label="Agency Amount"
                      value={`${selectedOrderForDetail.agency_total_amount || 0} ${selectedOrderForDetail.symbol || "USDT"}`}
                    />
                  </div>
                </Card>
              </SidebarFormGroup>
              );
            })()}

            {/* Invoice */}
            <InvoiceTable
              products={selectedOrderForDetail.product_details}
              symbol={selectedOrderForDetail.symbol}
              network={selectedOrderForDetail.network}
            />
          </div>
        )}
      </OrderDetailSidebar>

      {/* WebCam Modal */}
      {showWebCam && (
        <WebCam
          show={showWebCam}
          hide={() => setShowWebCam(false)}
          onCapture={handleCaptureImage}
        />
      )}

      {/* Custom Offer Modal */}
      {showOfferModal && selectedOffer && (
        <OfferModal
          modalIsOpen={showOfferModal}
          modalIsClose={() => {
            setShowOfferModal(false);
            setSelectedOffer(null);
          }}
          data={selectedOffer}
        />
      )}
    </PageContainer>
  );
}
